/**
 * normalizeFichaTecnica.js
 * ----------------------------------------------------------------------------
 * Transforma os itens "crus" de product.ficha_tecnica em PEÇAS DESENHÁVEIS.
 *
 * Princípios:
 *  - NUNCA recalcula preço aqui. Apenas exibe o que já existe.
 *  - SEMPRE preserva o item original em `rawItem` (rawItem é a fonte da verdade
 *    financeira; a peça normalizada é só a visão para desenho).
 *  - Converte números com vírgula ("2,40") para número real (2.40).
 *  - Mantém compatibilidade com o formato atual de `position` (percentual).
 *
 * Regra de cobrança (idêntica à do modal atual):
 *  - menor dimensão <= 0.10  => ML  (metro linear)
 *  - menor dimensão  > 0.10  => M²  (metro quadrado)
 */

/** Converte "2,40" | "2.40" | 2.4 | null em number seguro. */
export function toNumber(value) {
  if (typeof value === "number") return value;
  if (value === null || value === undefined || value === "") return 0;
  return parseFloat(`${value}`.replace(",", ".")) || 0;
}

export const DEFAULT_SLAB_THICKNESS_MM = 10;

export function normalizeThicknessMm(item) {
  const safeItem = item || {};
  const raw =
    safeItem.slabThicknessMm ??
    safeItem.espessura_mm ??
    safeItem.espessuraMm ??
    safeItem.grossura_mm ??
    safeItem.grossuraMm ??
    safeItem.slabThickness;
  const value = toNumber(raw);
  if (value > 0 && value < 1) return value * 1000;
  return value > 0 ? value : DEFAULT_SLAB_THICKNESS_MM;
}

/** Normaliza o texto do type (remove acentos e caixa) para comparação. */
export function normalizeType(type) {
  return `${type || ""}`
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();
}

export function isPrimaryPiece(item) {
  return normalizeType(item && item.type).includes("peca principal");
}

export function isSecondaryPiece(item) {
  return normalizeType(item && item.type).includes("peca secundaria");
}

/** Item do tipo "Acabamento" (serviço/adicional, NÃO a taxa percentual). */
export function isFinishingItem(item) {
  return normalizeType(item && item.type).includes("acabamento");
}

/** Peça de pedra desenhável como retângulo (principal ou secundária). */
export function isStonePiece(item) {
  return isPrimaryPiece(item) || isSecondaryPiece(item);
}

/**
 * Unidade de cobrança visual: M², ML ou UND.
 * Não altera cálculo, apenas classifica para exibir o rótulo correto.
 */
export function getBillingUnit(item) {
  if (isFinishingItem(item)) return "UND";
  if (!isStonePiece(item)) return "UND";
  const comprimento = toNumber(item.comprimento);
  const largura = toNumber(item.largura);
  const menorDimensao = Math.min(
    comprimento || Infinity,
    largura || Infinity
  );
  return menorDimensao <= 0.1 ? "ML" : "M2";
}

/** pricingType derivado da unidade de cobrança. */
export function getPricingType(item) {
  const unit = getBillingUnit(item);
  if (unit === "ML") return "linear_meter";
  if (unit === "M2") return "square_meter";
  return "unit";
}

/**
 * Posição padrão (percentual do canvas) quando o item ainda não tem `position`.
 * Mantém o mesmo "leque" usado hoje no modal para não mudar o layout salvo.
 */
export function createDefaultPosition(index = 0) {
  return {
    x: Math.min(90, 18 + (index % 5) * 12),
    y: Math.min(84, 20 + (index % 5) * 9),
    z: 0
  };
}

/** Papel visual usado para estilizar a peça no canvas. */
function getVisualRole(item) {
  if (isFinishingItem(item)) return "finishing";
  if (isSecondaryPiece(item)) return "secondary_piece";
  if (isPrimaryPiece(item)) return "main_piece";
  return "generic";
}

/**
 * Normaliza UM item da ficha em uma peça desenhável.
 * @param {object} item item cru da ficha_tecnica
 * @param {number} index índice original (sourceIndex)
 */
export function normalizePiece(item, index = 0) {
  const safeItem = item || {};
  const position = safeItem.position || createDefaultPosition(index);
  const finishing = isFinishingItem(safeItem);
  const slabThicknessMm = normalizeThicknessMm(safeItem);

  return {
    id: `piece_${String(index + 1).padStart(3, "0")}`,
    sourceIndex: index,
    sourceType: safeItem.type || "",
    description: safeItem.descricao || safeItem.descricao_personalizada || "",
    quantity: parseInt(safeItem.quantity) || 1,
    // dimensões em METROS (fonte da verdade)
    width: toNumber(safeItem.comprimento),
    height: toNumber(safeItem.largura),
    slabThicknessMm,
    slabThicknessM: slabThicknessMm / 1000,
    // posição em PERCENTUAL do canvas (compatível com o formato atual)
    position: {
      x: toNumber(position.x),
      y: toNumber(position.y),
      z: toNumber(position.z),
      basisWidth: toNumber(position.basisWidth),
      basisHeight: toNumber(position.basisHeight)
    },
    rotation: toNumber(safeItem.rotation),
    visualRole: getVisualRole(safeItem),
    pricingType: getPricingType(safeItem),
    billingUnit: getBillingUnit(safeItem),
    isPrimary: isPrimaryPiece(safeItem),
    isSecondary: isSecondaryPiece(safeItem),
    isFinishing: finishing,
    isStone: isStonePiece(safeItem),
    edgeFinishType: safeItem.edgeFinishType || "polida",
    edgeFinishes: Array.isArray(safeItem.edgeFinishes)
      ? [...safeItem.edgeFinishes]
      : [],
    // acabamento CADASTRADO por borda: { front: { insumoId, nome, imagem } }
    edgeFinishItems: safeItem.edgeFinishItems || {},
    // acabamento aplicado ao corpo/superfície da peça (distinto das arestas)
    surfaceFinishItem: safeItem.surfaceFinishItem || null,
    // identidade estável do item na ficha (relação entre peças)
    fichaId: safeItem.fichaId || null,
    // vínculo com a peça-mãe: { parentId: fichaId, edge: 'front'|... }
    linkedTo: safeItem.linkedTo || null,
    // relação com a principal: neutra | curva_baixo | curva_cima |
    // sobreposta_acima | sobreposta_abaixo (define o render em perspectiva)
    relacaoPrincipal:
      safeItem.relacaoPrincipal || safeItem.relacao_principal || "neutra",
    // lados preferidos para as cotas da peça. Ausente => automático.
    dimensionSides: safeItem.dimensionSides || null,
    // direção local do texto da peça no desenho (0/90/180/270 graus).
    labelTextDirection: toNumber(safeItem.labelTextDirection),
    // recortes vinculados (cuba/cooktop/furos) — metros relativos à peça.
    // Ausente em vendas antigas => [] (compat total).
    cutouts: Array.isArray(safeItem.cutouts)
      ? safeItem.cutouts.map(c => ({ ...c }))
      : [],
    // silhueta paramétrica (rect | L | U | custom). Ausente => retângulo.
    shape: safeItem.shape || null,
    // cantos arredondados unilaterais { tl, tr, br, bl } em metros.
    // Ausente => todos os cantos RETOS (padrão da marmoraria).
    cornerRadius: safeItem.cornerRadius || null,
    // chamadas de borda (ex.: "RODA BASE EM PEDRA" na frente)
    edgeLabels: safeItem.edgeLabels || {},
    total: toNumber(safeItem.total),
    // referência ao item original — fonte da verdade financeira
    rawItem: safeItem
  };
}

/**
 * Normaliza a ficha técnica inteira.
 * @param {Array} items product.ficha_tecnica
 * @returns {Array<piece>} peças normalizadas (inclui acabamentos marcados
 *                         com isFinishing para a camada de legenda).
 */
export function normalizeFichaTecnica(items) {
  if (!Array.isArray(items)) return [];
  return items.map((item, index) => normalizePiece(item, index));
}
